// =============================================================================
// Quote-review (maker-checker) shared helpers — role gate + status metadata.
//
// The approver set mirrors the backend's QuoteReviewController.IsApprover:
// the dedicated "CustomerService" role plus every admin-equivalent Epicor
// RoleCode (Admin / admin / Shukla / Testers / USAHedRep). Keep these in sync.
// =============================================================================
import { getUser } from "./auth";

const ADMIN_ROLECODES = ["admin", "shukla", "it", "testers", "usahedrep"];

/** True when the logged-in user may approve / return / create-now a conversion. */
export function isReviewApprover() {
  const role = (getUser().role || "").trim().toLowerCase();
  return role === "customerservice" || ADMIN_ROLECODES.includes(role);
}

// Status → friendly label + color "kind" (mirrors QuoteReviewMapper.Describe).
export const REVIEW_STATUS_META = {
  PendingReview:    { label: "In Review",         kind: "info",     stage: 2 },
  ChangesRequested: { label: "Returned to Rep",   kind: "warning",  stage: 2 },
  Approved:         { label: "Approved — Queued", kind: "info",     stage: 3 },
  Creating:         { label: "Creating Order…",   kind: "progress", stage: 4 },
  OrderCreated:     { label: "Order Created",     kind: "success",  stage: 5 },
  CreationFailed:   { label: "Needs Attention",   kind: "error",    stage: 4 },
};

export function reviewStatusMeta(status) {
  return REVIEW_STATUS_META[status] || { label: status || "Unknown", kind: "info", stage: 0 };
}

/** statusKind → an accent color (matches the convert modal's status tracker). */
export function statusColor(kind) {
  return { info: "#0052A5", progress: "#6366F1", success: "#10B981", warning: "#B45309", error: "#DC2626" }[kind] || "#64748B";
}

/** A react-bootstrap Badge bg for a systemCheck value. */
export function systemCheckBg(sc) {
  return sc === "Ready" ? "success" : sc === "Hold" ? "warning" : sc === "NotReady" ? "danger" : "secondary";
}

// Server-computed automated gate (QuoteReviewController: SystemCheck) → the
// error-status classification shown on the review queue. "Ready" is the only
// clear-to-convert state; the rest are holds the CS reviewer must resolve.
//   • Hold     → coverage minimum met, but uploaded PO(s) are unconfirmed/held
//   • NotReady → coverage minimum NOT met (quote is under-covered / short)
export const SYSTEM_CHECK_META = {
  Ready:    { key: "Ready",    label: "Ready to convert",     tone: "success",   dark: false },
  Hold:     { key: "Hold",     label: "PO held — needs review", tone: "warning", dark: true  },
  NotReady: { key: "NotReady", label: "Under-covered",        tone: "danger",    dark: false },
  Unknown:  { key: "Unknown",  label: "Not checked",          tone: "secondary", dark: false },
};

/** Classify a review row's automated gate (systemCheck) into a display category. */
export function systemCheckMeta(sc) {
  return SYSTEM_CHECK_META[sc] || { key: sc || "Unknown", label: sc || "Unknown", tone: "secondary", dark: false };
}
